//Proofed
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import DescriptionIcon from '@mui/icons-material/Description'
import FilterListIcon from '@mui/icons-material/FilterList'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import SearchIcon from '@mui/icons-material/Search'
import AssessmentIcon from '@mui/icons-material/Assessment'
import type { HowItWorksStep } from '../components/HowItWorksSection'

// Hero right-panel highlights
export const bidPartnerHighlights = [
    'Access a network of experienced UK public sector bid writers',
    'Scale your bid writing capacity without a full-time hire',
    'Match you with a writer who knows your sector and buyer landscape',
    'Flexible support for a single bid, a retainer or a dedicated writer',
    'Expert review and quality assurance on every submission',
]

// Challenge cards  section 2
export const bidPartnerChallenges = [
    {
        heading: "You're winning opportunities but don't have the bid writing capacity to pursue them all",
        body: "Identifying the right tenders is only half the battle. If your team can't write and submit at the rate the market demands, you leave contracts on the table purely through lack of bandwidth",
    },
    {
        heading: "Hiring a full-time bid writer is premature but you need expert support now",
        body: "A permanent bid writer makes sense at scale. But if you're growing into public sector or entering a new phase, a flexible arrangement with a specialist writer gives you the capacity you need without the overhead, commitment or 6-month onboarding",
    },
    {
        heading: "You've lost bids you should have won. The problem is the writing, not your capability",
        body: "Most unsuccessful bids fail on structure, evidence and presentation, not on the underlying capability of the business. An experienced public sector bid writer knows exactly what evaluators are looking for and how to position your offering to score",
    }
]

// Recommended solution stack  section 3
export const bidPartnerSolutionStack = [
    {
        Icon: SearchIcon,
        title: 'Bid Writer Matching',
        body: 'We connect you with a specialist bid writer from our network who has direct experience in your sector and with your buyer type. You get the right person for the specific contract',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
    {
        Icon: AssessmentIcon,
        title: 'Bid Writing & Submission Support',
        body: 'Your matched writer takes ownership of the bid, structuring responses, writing compelling answers, integrating your evidence and managing the submission process. You review and approve and they handle the detail',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
    {
        Icon: QueryStatsIcon,
        title: 'Quality Review & Feedback',
        body: 'Every submission goes through a quality review before it goes out and whether you win or lose, we provide structured feedback on your bid performance so your submissions get sharper with every iteration',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
]

// Journey steps  section 4
export const bidPartnerSteps: HowItWorksStep[] = [
    {
        num: '1',
        Icon: QueryStatsIcon,
        title: 'Brief & Match',
        body: 'You tell us the contract, the sector and the deadline. We match you with an experienced bid writer from our network who has relevant sector knowledge and buyer familiarity',
    },
    {
        num: '2',
        Icon: DescriptionIcon,
        title: 'Discovery & Planning',
        body: 'Your writer reviews the tender documents, assesses the requirements and works with you to build a win strategy, identifying your strongest evidence and the themes that will score highest',
    },
    {
        num: '3',
        Icon: FilterListIcon,
        title: 'Write & Review',
        body: 'Your writer drafts the full response. You review for accuracy and approve. We build in a quality review pass before anything goes near the submission portal',
    },
    {
        num: '4',
        Icon: EmojiEventsIcon,
        title: 'Submit & Learn',
        body: 'The completed bid is submitted on time and to the required standard. Whatever the outcome, we provide structured feedback so every bid improves your next submission',
    },
]

// Outcomes checklist  section 5
export const bidPartnerOutcomes = [
    'Bid writing capacity that scales with your pipeline, without a permanent hire',
    'Submissions written by specialists who understand public sector evaluation criteria',
    'A matched writer with direct experience in your sector and buyer type',
    'Quality-reviewed bids that represent your business at its strongest',
    'Structured feedback after every submission, win or lose',
]

// Self-qualification checklist  section 6
export const bidPartnerQualifiers = [
    "You're actively identifying tenders but don't have the internal capacity to write them all",
    "You want a specialist writer, not a generalist. You want someone who knows your sector and how buyers score",
    "A permanent bid writer isn't the right move yet, but ad hoc or retained support is",
    "You've lost bids you expected to win and want to understand why and fix it",
    "You need a scalable writing solution that grows with your public sector pipeline",
]



// FAQs  section 8
export const bidPartnerFaqs = [
    {
        q: 'How does the bid writer matching process work?',
        a: "Once you brief us on the contract details (sector, buyer, value, timeline), we identify the most appropriate writer from our network. We look for someone with direct experience in your sector and with the relevant buyer type (central government, NHS, local authority, etc.). You meet them before the engagement begins so you can confirm the fit.",
    },
    {
        q: 'Can I work with the same writer on multiple bids?',
        a: "Yes and for most clients this is the preferred arrangement. Once a writer is familiar with your business, your case studies and your positioning, subsequent bids are faster and sharper. We can arrange retained engagements where a dedicated writer is available to your pipeline on an ongoing basis.",
    },
    {
        q: 'How much involvement is needed from our team?',
        a: "Primarily in the early stages, helping your writer understand your business, your capabilities and the evidence available. Once that knowledge base is built, day-to-day involvement is light. You'll review and approve drafts before submission, but the writing, structuring and project management sit with the writer.",
    },
    {
        q: 'How is the service priced?',
        a: "Bid writing support is available on a per-bid basis or as a retained arrangement. Per-bid pricing depends on the complexity and volume of questions in the ITT/PQQ. Retained engagements are priced monthly based on the expected volume of activity. We'll confirm pricing on the discovery call. There are no hidden charges.",
    },
    {
        q: 'What if we lose the bid?',
        a: "Every engagement includes structured post-submission feedback: a review of your submission against the scoring criteria, an assessment of where marks were won and lost and specific recommendations for your next bid. Losing a bid is only wasted effort if you don't learn from it. We make sure you do.",
    },
    {
        q: 'How is this different from CFP Bid Services?',
        a: "CFP Bid Services covers specific advisory engagements: Bid Prep Days, Win Reviews and Market Research. The Bid Partner service is different: it's about connecting you with a specialist writer who actually writes your bids alongside you. It's practical, hands-on support from someone who knows your sector.",
    },
]

// Related solution links  section 9
export const bidPartnerRelated = [
    {
        headline: 'Improve Efficiency & Win Rates',
        body: 'Want to combine expert bid writing with the platform tools that improve your pipeline? Core Pro and Tender Ninja give your writers and your team, a competitive edge on every bid.',
        link: '/solutions/improve-efficiency-and-win-rates',
        tag: 'Execution Support',
    },

]
